import { useCallback, useEffect, useMemo, useState } from 'react';
import { IS_SERVER } from '../ssr';
import { useLocalStorage } from './useLocalStorage';
import { useMediaQuery } from './useMediaQuery';

interface IBeforeInstallPrompt {
  readonly platforms: string[];
}

interface IBeforeInstallPromptEvent extends IBeforeInstallPrompt, Event {
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

// Must be cached because beforepromptinstall only runs once per page
let cachedEvent: IBeforeInstallPromptEvent | IBeforeInstallPrompt | null = null;
let cachedInstalled = false;

export function useAddToHomescreenPrompt() {
  const [hideAppPromptTime, setHideAppPromptTime] = useLocalStorage<string | number | undefined>(
    'hideappprompt',
    undefined,
  );

  const [promptable, setPromptable] = useState<IBeforeInstallPromptEvent | null>(
    cachedEvent as IBeforeInstallPromptEvent,
  );

  const isPwa = useMediaQuery('(display-mode: standalone)', true);
  const [isInstalled, setIsInstalled] = useState(isPwa || cachedInstalled);

  const promptType = useMemo(() => {
    if (promptable?.prompt) {
      return 'app' as const;
    }

    if (!IS_SERVER && promptable?.platforms?.[0]?.startsWith('safari:')) {
      return promptable?.platforms?.[0] as 'safari:macos' | 'safari:ios';
    }
  }, [promptable]);

  const promptToInstall = useCallback(() => {
    if (promptable?.prompt) {
      return promptable.prompt();
    }
    return Promise.reject(
      new Error('Tried installing before browser sent "beforeinstallprompt" event'),
    );
  }, [promptable]);

  useEffect(() => {
    const ready = (e: IBeforeInstallPromptEvent) => {
      cachedEvent = e;

      e.preventDefault();
      setPromptable(e);
    };

    if (!IS_SERVER) {
      window.addEventListener('beforeinstallprompt', ready as EventListenerOrEventListenerObject);

      return () => {
        window.removeEventListener(
          'beforeinstallprompt',
          ready as EventListenerOrEventListenerObject,
        );
      };
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies(isPwa): Uses skipBinding so it doesn't change
  useEffect(() => {
    const onInstall = () => {
      setIsInstalled(true);
      cachedInstalled = true;
    };

    if (!IS_SERVER) {
      window.addEventListener('appinstalled', onInstall as EventListenerOrEventListenerObject);

      if (
        !isPwa &&
        // Chrome/Chromium also includes "Safari" in its UserAgent... fun
        !/Chrom/i.test(navigator.userAgent) &&
        !/Edge/i.test(navigator.userAgent) &&
        /Safari/i.test(navigator.userAgent)
      ) {
        const platform = `${
          typeof navigator !== 'undefined' &&
          (navigator.userAgentData?.platform || navigator.platform)
        }`;

        const isIos = platform.startsWith('i');

        cachedEvent = {
          platforms: [isIos ? 'safari:ios' : 'safari:macos'],
        };
        setPromptable(cachedEvent as IBeforeInstallPromptEvent);
      }

      return () => {
        window.removeEventListener('appinstalled', onInstall as EventListenerOrEventListenerObject);
      };
    }
  }, []);

  const ret = useMemo(
    () => ({
      promptType,
      promptable,
      promptToInstall,
      isInstalled,
      hideAppPromptTime,
      setHideAppPromptTime,
    }),
    [promptType, promptable, promptToInstall, isInstalled, hideAppPromptTime],
  );

  return ret;
}
